/**
 * LiveTicker — top-of-page marquee with real-time data.
 *
 * Style follows the editorial-data-art brief: black bar, ochre divider dots,
 * uppercase mono. Re-fetches /api/pulse/now every 5 minutes via the parent.
 */
import React from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";

const Dot = ({ tone = "ochre" }) => (
  <motion.span
    className={`inline-block w-2 h-2 rounded-full mx-5 ${
      tone === "alert" ? "bg-[#FF5500]" : "bg-[#00FA9A]"
    }`}
    animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
    transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
  />
);

export default function LiveTicker({ pulse }) {
  if (!pulse) return null;
  const items = [];
  if (pulse.top_stress?.label && pulse.top_stress.ndvi != null) {
    items.push({
      t: `STRESS-HOTSPOT: ${pulse.top_stress.label} · NDVI ${pulse.top_stress.ndvi.toFixed(2)}`,
      alert: true,
    });
  }
  if (pulse.bees) {
    items.push({
      t: `BIENENFLUG: ${pulse.bees.label.toUpperCase()} (${pulse.bees.avg_temp}°C ⌀)`,
    });
  }
  if (pulse.wildfires) {
    const w = pulse.wildfires;
    items.push({
      t: `WALDBRAND-MONITOR: ${w.europe_24h} in Europa · ${w.deat_24h} DE/AT · 24h`,
      alert: (w.risk_level || "").toLowerCase() !== "niedrig",
    });
  }
  items.push({
    t: `FROSTGEFAHR: ${pulse.alerts.frost_count === 0 ? "ENTWARNUNG" : `${pulse.alerts.frost_count} REGIONEN`}`,
    alert: pulse.alerts.frost_count > 0,
  });
  if (pulse.alerts.heat_count > 0) {
    items.push({
      t: `HITZEWARNUNG: ${pulse.alerts.heat_count} REGIONEN`,
      alert: true,
    });
  }
  if (pulse.top_vital) {
    items.push({
      t: `VITAL-HIGHLIGHT: ${pulse.top_vital.label} · NDVI ${pulse.top_vital.ndvi.toFixed(2)}`,
    });
  }
  items.push({ t: `SAISON: ${pulse.season.toUpperCase()}` });
  items.push({
    t: `${pulse.stats.regions_monitored} REGIONEN · 2 SATELLITEN · FREI ZUGÄNGLICH`,
  });

  return (
    <div
      className="bg-[#0A0A0A] text-white py-2.5 overflow-hidden border-y border-white/10"
      data-testid="live-ticker"
    >
      <Marquee speed={45} pauseOnHover gradient={false} className="font-mono text-[12px] tracking-[0.15em]">
        {items.map((it, i) => (
          <span key={i} className="flex items-center">
            <Dot tone={it.alert ? "alert" : "ochre"} />
            <span className={it.alert ? "text-[#FF7A45]" : "text-white"}>
              {it.t}
            </span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}
